import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Search, X, MessageSquare, Loader2, CalendarDays } from "lucide-react";

import SelectMenu from "./dashboard5/SelectMenu";
import { authFetch } from "../utils/auth";

// The sidebar shows the 30 most recent chats; this modal is the rest of them.
// Asked for at the endpoint's ceiling so "All chats" is genuinely all of them
// rather than a second, slightly longer page with the same problem.
const ALL_CHATS_LIMIT = 500;

const DAY_MS = 86400000;

const RANGES = [
  { key: "all", label: "Any time" },
  { key: "today", label: "Today" },
  { key: "7d", label: "Last 7 days" },
  { key: "30d", label: "Last 30 days" },
  { key: "custom", label: "Custom" },
];

const startOfDay = (value) => {
  const d = new Date(value);
  d.setHours(0, 0, 0, 0);
  return d;
};

/**
 * Heading for a day bucket.
 *
 * Relative for the days people actually think in relative terms about, absolute
 * past that — "12 days ago" is a subtraction the reader has to do, where a date
 * is something they can match against their own memory of the week.
 */
const dayLabel = (date) => {
  const today = startOfDay(new Date());
  const diff = Math.round((today - startOfDay(date)) / DAY_MS);
  if (diff <= 0) return "Today";
  if (diff === 1) return "Yesterday";
  if (diff < 7) return date.toLocaleDateString(undefined, { weekday: "long" });
  return date.toLocaleDateString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
    ...(date.getFullYear() === today.getFullYear() ? {} : { year: "numeric" }),
  });
};

const timeLabel = (date) =>
  date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });

/**
 * AllChatsModal — the full session history behind the sidebar's "View all".
 *
 * `seed` is the list the sidebar already has on screen. Rendering it first means
 * the modal opens populated and fills in when the wider fetch lands, instead of
 * flashing a spinner over rows the user could already see a moment ago.
 */
const AllChatsModal = ({ open, onClose, onSelect, activeSessionId = "", seed = [], surface = "" }) => {
  const [sessions, setSessions] = useState(seed);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [query, setQuery] = useState("");
  const [range, setRange] = useState("all");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [product, setProduct] = useState("");
  const searchRef = useRef(null);

  // Fetch on every open rather than once: a chat started since the last open
  // has to be in the list, and this is the only moment the user is waiting on it.
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    (async () => {
      try {
        // Same scope as the rail that opened this. Without it "View all chats"
        // is a way around the enterprise rail's surface filter — it would list
        // every session the account owns, copilot ones included.
        const scope = surface ? `&surface=${encodeURIComponent(surface)}` : "";
        const res = await authFetch(`/api/copilot/sessions?limit=${ALL_CHATS_LIMIT}${scope}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (cancelled) return;
        setSessions(
          (data.sessions || []).map((s) => ({
            id: s.session_id,
            title: s.title || "Untitled session",
            product: s.product || "",
            updatedAt: s.updated_at || null,
            closed: !!s.closed,
          }))
        );
      } catch {
        // The seeded rows stay on screen — a partial list beats an empty one.
        if (!cancelled) setFailed(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, surface]);

  // Reset the filters between openings. Leaving a date range armed from last
  // time is the kind of state that reads as "my chats are gone".
  useEffect(() => {
    if (open) {
      setQuery("");
      setRange("all");
      setCustomFrom("");
      setCustomTo("");
      setProduct("");
      setSessions((prev) => (prev.length ? prev : seed));
    }
    // `seed` is deliberately not a dependency: it changes identity on every
    // sidebar fetch, and re-running this would wipe the user's filters mid-use.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    // Behind a frame so the click that opened the modal cannot steal it back.
    const t = setTimeout(() => searchRef.current?.focus(), 0);
    return () => {
      window.removeEventListener("keydown", onKey);
      clearTimeout(t);
    };
  }, [open, onClose]);

  const products = useMemo(() => {
    const seen = new Set();
    sessions.forEach((s) => { if (s.product) seen.add(s.product); });
    return [...seen].sort();
  }, [sessions]);

  // [from, to) in local time, or null for no bound. `to` is the START of the day
  // after the one picked, so a range ending "today" includes today's chats
  // rather than only those at exactly 00:00.
  const bounds = useMemo(() => {
    const today = startOfDay(new Date());
    if (range === "today") return [today, null];
    if (range === "7d") return [new Date(today - 6 * DAY_MS), null];
    if (range === "30d") return [new Date(today - 29 * DAY_MS), null];
    if (range === "custom") {
      return [
        customFrom ? startOfDay(customFrom) : null,
        customTo ? new Date(startOfDay(customTo).getTime() + DAY_MS) : null,
      ];
    }
    return [null, null];
  }, [range, customFrom, customTo]);

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const [from, to] = bounds;

    const matched = sessions.filter((s) => {
      if (q
        && !String(s.title || "").toLowerCase().includes(q)
        && !String(s.product || "").toLowerCase().includes(q)) return false;
      if (product && s.product !== product) return false;
      if (from || to) {
        // A row with no timestamp cannot be placed on the calendar, so it is not
        // claimed by any date filter — showing it under an arbitrary day would
        // be worse than leaving it out of a deliberately narrowed view.
        if (!s.updatedAt) return false;
        const t = new Date(s.updatedAt).getTime();
        if (Number.isNaN(t)) return false;
        if (from && t < from.getTime()) return false;
        if (to && t >= to.getTime()) return false;
      }
      return true;
    });

    // Newest first, undated rows on top: the only rows without a timestamp are
    // chats created seconds ago that Postgres has not caught up with.
    const sorted = matched.slice().sort((a, b) => {
      const ta = a.updatedAt ? new Date(a.updatedAt).getTime() : Infinity;
      const tb = b.updatedAt ? new Date(b.updatedAt).getTime() : Infinity;
      return tb - ta;
    });

    const out = [];
    let current = null;
    sorted.forEach((s) => {
      const date = s.updatedAt ? new Date(s.updatedAt) : new Date();
      const label = Number.isNaN(date.getTime()) ? "Earlier" : dayLabel(date);
      if (!current || current.label !== label) {
        current = { label, rows: [] };
        out.push(current);
      }
      current.rows.push({ ...s, date });
    });
    return out;
  }, [sessions, query, product, bounds]);

  const shown = groups.reduce((n, g) => n + g.rows.length, 0);

  const choose = useCallback((id) => {
    onSelect?.(id);
    onClose?.();
  }, [onSelect, onClose]);

  if (!open) return null;

  return createPortal(
    // Above the sidebar's own z-[60], and portalled to the body because the
    // sidebar is `overflow-hidden` — rendered in place it would be clipped to a
    // 240px column.
    <div
      className="fixed inset-0 z-[200] flex items-start justify-center bg-black/30 px-4 py-10 backdrop-blur-[2px]"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="All chats"
        className="flex max-h-full w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
        style={{ fontFamily: "'Space Grotesk', sans-serif" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-3 border-b border-gray-100 px-5 py-4">
          <div>
            <h2 className="text-[15px] font-bold text-[#253439]">All chats</h2>
            <p className="text-[11px] text-gray-400">
              {loading && sessions.length === 0
                ? "Loading…"
                : `${shown} of ${sessions.length} ${sessions.length === 1 ? "chat" : "chats"}`}
              {loading && sessions.length > 0 ? " · refreshing" : ""}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
          >
            <X size={16} />
          </button>
        </div>

        {/* Search + filters */}
        <div className="flex flex-col gap-2 border-b border-gray-100 px-5 py-3">
          <div className="relative">
            <Search
              size={14}
              aria-hidden="true"
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-300"
            />
            <input
              ref={searchRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search all chats"
              aria-label="Search all chats"
              className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-9 pr-8 text-[13px] text-gray-700 placeholder-gray-400 outline-none transition-colors focus:border-cyan-300"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                aria-label="Clear search"
                className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded p-0.5 text-gray-400 transition-colors hover:text-cyan-700"
              >
                <X size={13} />
              </button>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-1.5">
            <CalendarDays size={13} aria-hidden="true" className="text-gray-300" />
            {RANGES.map((r) => (
              <button
                key={r.key}
                type="button"
                onClick={() => setRange(r.key)}
                aria-pressed={range === r.key}
                className={`rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors ${
                  range === r.key
                    ? "bg-cyan-500 text-white"
                    : "bg-gray-100 text-gray-500 hover:bg-cyan-50 hover:text-cyan-700"
                }`}
              >
                {r.label}
              </button>
            ))}
            {products.length > 0 && (
              <SelectMenu
                value={product}
                onChange={setProduct}
                options={[
                  { value: "", label: "All products" },
                  ...products.map((p) => ({ value: p, label: p })),
                ]}
                ariaLabel="Filter by product"
                className="ml-auto"
              />
            )}
          </div>

          {range === "custom" && (
            <div className="flex items-center gap-2 text-[11px] text-gray-500">
              <label className="flex items-center gap-1.5">
                From
                <input
                  type="date"
                  value={customFrom}
                  max={customTo || undefined}
                  onChange={(e) => setCustomFrom(e.target.value)}
                  className="rounded-lg border border-gray-200 px-2 py-1 text-[11px] text-gray-600 outline-none focus:border-cyan-300"
                />
              </label>
              <label className="flex items-center gap-1.5">
                To
                <input
                  type="date"
                  value={customTo}
                  min={customFrom || undefined}
                  onChange={(e) => setCustomTo(e.target.value)}
                  className="rounded-lg border border-gray-200 px-2 py-1 text-[11px] text-gray-600 outline-none focus:border-cyan-300"
                />
              </label>
            </div>
          )}
        </div>

        {/* Grouped list */}
        <div className="min-h-0 flex-1 overflow-y-auto d5-recents-scroll px-2 pb-2">
          {loading && sessions.length === 0 && (
            <div className="flex items-center justify-center py-10 text-gray-400">
              <Loader2 size={18} className="animate-spin" />
            </div>
          )}
          {!loading && failed && sessions.length === 0 && (
            <p className="px-3 py-10 text-center text-[12px] text-gray-400">
              Could not load your chats. Close this and try again.
            </p>
          )}
          {!loading && !failed && sessions.length > 0 && shown === 0 && (
            <p className="px-3 py-10 text-center text-[12px] text-gray-400">
              No chats match these filters.
            </p>
          )}
          {!loading && sessions.length === 0 && !failed && (
            <p className="px-3 py-10 text-center text-[12px] text-gray-400">No chats yet.</p>
          )}

          {groups.map((group) => (
            <div key={group.label} className="mb-1">
              {/* Sticky so the day the reader is scrolling through stays named.
                  OPAQUE and full-bleed on purpose: bg-white/95 + backdrop-blur
                  let the row underneath ghost through as it scrolled past, and
                  -mx-2 pushes the band out over the scroller's own px-2 so no
                  sliver of that row survives at either edge. px-5 puts the label
                  back in line with the rows' px-3.

                  mb-1 is load-bearing, not spacing taste: the active row marks
                  itself with `ring-1`, which Tailwind draws as an OUTSET
                  box-shadow occupying the 1px band immediately above the
                  button. Sticky + z-10 puts this label in its own stacking
                  context painted above that band, so with no gap the selected
                  first row of a group lost its top edge entirely. */}
              <p className="sticky top-0 z-10 -mx-2 mb-1 border-b border-gray-100 bg-white px-5 py-2 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                {group.label}
              </p>
              {group.rows.map((entry) => {
                const isActive = !!activeSessionId && entry.id === activeSessionId;
                return (
                  <button
                    key={entry.id}
                    type="button"
                    onClick={() => choose(entry.id)}
                    title={entry.title}
                    aria-current={isActive ? "page" : undefined}
                    className={`group flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left transition-colors ${
                      isActive ? "bg-cyan-50 ring-1 ring-cyan-200" : "hover:bg-cyan-50"
                    }`}
                  >
                    <MessageSquare
                      size={14}
                      className={`flex-shrink-0 ${isActive ? "text-cyan-600" : "text-gray-300 group-hover:text-cyan-600"}`}
                    />
                    <span className="min-w-0 flex-1">
                      <span className={`block truncate text-[13px] leading-tight ${
                        isActive ? "font-medium text-cyan-800" : "text-gray-700 group-hover:text-cyan-800"
                      }`}>
                        {entry.title}
                      </span>
                      <span className="block truncate text-[10px] leading-tight text-gray-400">
                        {entry.product ? `${entry.product} · ` : ""}
                        {entry.updatedAt && !Number.isNaN(entry.date.getTime())
                          ? timeLabel(entry.date)
                          : "Saving…"}
                      </span>
                    </span>
                    {entry.closed && (
                      <span className="ml-auto flex-shrink-0 rounded-full border border-gray-200 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-gray-400">
                        Closed
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AllChatsModal;
